import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Text,
  VStack,
  Wrap,
} from '@chakra-ui/react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { FiPaperclip, FiX } from 'react-icons/fi';

import { ChatComposerRow } from '#components/chat-composer-row';
import { MessageList } from '#components/message-list';
import { PageContentSidebarPortal } from '#components/page-content-sidebar-portal';
import { toaster } from '#components/ui/toaster';
import { MESSAGE_ATTACHMENT_UPLOAD_IDS } from '#constants/message-context-names';
import { mergeCreateMessageIntoGetMessagesCache } from '#graphql/cache/merge-create-message-into-get-messages';
import { CREATE_MESSAGE } from '#graphql/mutations/create-message';
import { DELETE_UPLOAD } from '#graphql/mutations/delete-upload';
import { useDocumentScopedMessages } from '#hooks/use-document-scoped-messages';
import { useScrollMessageListToEnd } from '#hooks/use-scroll-message-list-to-end';
import { useAppConfig } from '#providers/app-config-provider';
import { usePageContentSidebar } from '#providers/page-layout-provider';
import { useCurrentUser } from '#providers/user-provider';

import {
  hasComposerContent,
  useDocumentChatComposer,
} from '../document-chat-composer-context';
import { useDocumentContext } from '../document-context';
import { stringifyEditorJs } from '../editorjs-types';
import {
  composerAttachmentMarkdownLine,
  uploadFileForMessageDraft,
} from '../upload-image';

import { useEditDocument } from './edit-document-context';

export interface DocumentChatFooterProps {
  /** When false, sidebar chat UI is hidden (e.g. narrow layout); the page content sidebar column is hidden too. */
  active?: boolean;
}

/**
 * Document chat: same thread list / composer behavior as the dashboard, scoped to this document.
 * Send creates the user message (with optional editor snapshot context). To run Ask AI, the user
 * adds the `ask-ai` reaction on that message (see {@link MessageCard}).
 * Portaled into {@link PageContentSidebar}.
 */
export function DocumentChatFooter({ active = true }: DocumentChatFooterProps) {
  const { config } = useAppConfig();
  const apiHost = config.apiHost;
  const { documentId, isNewDocument } = useDocumentContext();
  const user = useCurrentUser();
  const { setContentSidebarSlotActive, setContentSidebarDismissed } =
    usePageContentSidebar();
  const prevActiveRef = useRef<boolean | undefined>(undefined);

  useLayoutEffect(() => {
    setContentSidebarSlotActive(active);
    return () => {
      setContentSidebarSlotActive(false);
      setContentSidebarDismissed(true);
    };
  }, [active, setContentSidebarSlotActive, setContentSidebarDismissed]);

  useEffect(() => {
    if (active && prevActiveRef.current === false) {
      setContentSidebarDismissed(false);
    }

    prevActiveRef.current = active;
  }, [active, setContentSidebarDismissed]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    displayMessages,
    focusedRootId,
    isMessagesInitialLoad,
    messagesError,
    refetchMessages,
    expandThread,
    collapseThread,
    messagesFeatureActive,
    getMessagesVariables,
  } = useDocumentScopedMessages(documentId, {
    enabled: active && Boolean(documentId),
  });

  useScrollMessageListToEnd(messagesEndRef, displayMessages);

  const { getValue } = useEditDocument();

  const {
    promptText,
    setPromptText,
    clear,
    composerRootRef,
    pendingAttachments,
    addPendingAttachment,
    removePendingAttachment,
  } = useDocumentChatComposer();

  const [createMessage, { loading: createMessageLoading }] =
    useMutation(CREATE_MESSAGE);
  const [deleteUpload] = useMutation(DELETE_UPLOAD);
  const [attachmentUploadBusy, setAttachmentUploadBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSendMessage = async () => {
    if (!hasComposerContent(promptText, { pendingAttachments })) return;

    if (!documentId) return;

    const trimmed = promptText.trim();
    const bodyParts: string[] = [];
    if (trimmed.length > 0) {
      bodyParts.push(trimmed);
    }

    for (const att of pendingAttachments) {
      bodyParts.push(
        composerAttachmentMarkdownLine(
          att.filename,
          att.id,
          att.url,
          att.isEncrypted,
          apiHost,
        ),
      );
    }
    const value = bodyParts.join('\n\n');
    const hadNoThreadFocus = !focusedRootId;
    try {
      const contexts: { name: string; value: string }[] = [];
      if (!focusedRootId && documentId) {
        contexts.push({ name: 'documentId', value: documentId });
      }

      const snap = stringifyEditorJs(getValue() ?? null);
      if (snap.length > 0) {
        contexts.push({ name: 'currentEditorValue', value: snap });
      }

      if (pendingAttachments.length > 0) {
        contexts.push({
          name: MESSAGE_ATTACHMENT_UPLOAD_IDS,
          value: JSON.stringify(pendingAttachments.map((a) => a.id)),
        });
      }

      const { data } = await createMessage({
        variables: {
          input: {
            value,
            parentMessageId: focusedRootId ?? undefined,
            ...(contexts.length > 0 ? { contexts } : {}),
          },
        },
        update(cache, { data: mutationData }) {
          if (mutationData?.createMessage) {
            mergeCreateMessageIntoGetMessagesCache(
              cache,
              mutationData.createMessage,
              getMessagesVariables,
            );
          }
        },
      });

      const createdId = data?.createMessage?.id;
      if (!createdId) {
        toaster.create({
          title: 'Failed to send message',
          description: 'Create message returned no id.',
          type: 'error',
        });
        return;
      }

      await refetchMessages();

      clear();
      if (hadNoThreadFocus) {
        expandThread(createdId);
      }
    } catch (error) {
      toaster.create({
        title: 'Failed to send message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const onAttachmentFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;

      try {
        setAttachmentUploadBusy(true);
        const result = await uploadFileForMessageDraft(file, { apiHost });
        addPendingAttachment({
          id: result.id,
          filename: file.name,
          url: result.url,
          isEncrypted: result.isEncrypted,
        });
      } catch (error) {
        toaster.create({
          title: 'Upload failed',
          description: error instanceof Error ? error.message : 'Unknown error',
          type: 'error',
        });
      } finally {
        setAttachmentUploadBusy(false);
      }
    },
    [addPendingAttachment, apiHost],
  );

  const removeDraftAttachment = useCallback(
    async (id: string) => {
      try {
        await deleteUpload({ variables: { id } });
      } catch (error) {
        toaster.create({
          title: 'Could not remove file',
          description: error instanceof Error ? error.message : 'Unknown error',
          type: 'error',
        });
        return;
      }
      removePendingAttachment(id);
    },
    [deleteUpload, removePendingAttachment],
  );

  const composerDisabled =
    isNewDocument || createMessageLoading || attachmentUploadBusy;

  const placeholder = messagesFeatureActive
    ? focusedRootId
      ? 'Type a reply…'
      : 'Type a message…'
    : isNewDocument
      ? 'Save the document first to use document messages'
      : 'Type a message…';

  const footerContent = (
    <VStack
      gap={2}
      align="stretch"
      width="100%"
      flex={1}
      minH={0}
      height="100%"
    >
      {!documentId && (
        <Text fontSize="xs" color="fg.muted">
          Save the document to use document messages.
        </Text>
      )}
      {messagesFeatureActive ? (
        <>
          <Box
            flex={1}
            minH={0}
            overflow="hidden"
            display="flex"
            flexDirection="column"
            width="100%"
          >
            <MessageList
              messages={displayMessages}
              loading={isMessagesInitialLoad}
              error={messagesError ?? undefined}
              emptyMessage="No messages for this document yet."
              focusedRootId={focusedRootId}
              onExpandThread={expandThread}
              messagesEndRef={messagesEndRef}
              alignMessagesToBottom
              onMessageDeleted={() => void refetchMessages()}
            />
          </Box>
          {focusedRootId ? (
            <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
              <Text>Replying in thread</Text>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => collapseThread()}
              >
                Cancel
              </Button>
            </HStack>
          ) : null}
        </>
      ) : null}
      {user.email ? (
        <>
          {pendingAttachments.length > 0 ? (
            <Wrap gap={1}>
              {pendingAttachments.map((att) => (
                <HStack
                  key={att.id}
                  gap={1}
                  px={2}
                  py={1}
                  borderRadius="md"
                  bg="bg.subtle"
                  fontSize="xs"
                  maxW="100%"
                >
                  <Text truncate title={att.filename}>
                    {att.filename}
                  </Text>
                  <IconButton
                    aria-label={`Remove ${att.filename}`}
                    size="xs"
                    variant="ghost"
                    onClick={() => void removeDraftAttachment(att.id)}
                  >
                    <FiX />
                  </IconButton>
                </HStack>
              ))}
            </Wrap>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={(ev) => void onAttachmentFileChange(ev)}
          />
          <ChatComposerRow
            rootRef={composerRootRef}
            value={promptText}
            onChange={setPromptText}
            onSubmit={() => void handleSendMessage()}
            placeholder={placeholder}
            disabled={composerDisabled}
            loading={createMessageLoading}
            autoGrow
            submitDisabled={
              !hasComposerContent(promptText, { pendingAttachments })
            }
            trailingActions={
              <IconButton
                type="button"
                aria-label="Attach file"
                title="Attach file"
                size="sm"
                variant="outline"
                loading={attachmentUploadBusy}
                disabled={composerDisabled}
                onClick={() => fileInputRef.current?.click()}
              >
                <FiPaperclip />
              </IconButton>
            }
          />
        </>
      ) : null}
    </VStack>
  );

  if (!active) {
    return null;
  }

  return <PageContentSidebarPortal>{footerContent}</PageContentSidebarPortal>;
}
